import axios from 'axios'
import axiosRetry from 'axios-retry'
import { compact, orderBy, uniqBy } from 'lodash-es'

import {
  Address,
  GOOGLE_PLACES_UPDATED_API_URL,
  GOOGLE_PLACES_VENDOR_API_URL,
  LocationMatcherDisplayFormats,
  STATES,
} from '@lyrahealth-inc/shared-app-logic'

/**
 * Helper function to determine if an address is outside of US by
 * determining whether or not the address has an invalid state value.
 */
const isOUSAddress = (address: Address) => {
  // Get list of state values
  const stateValues = STATES.map((state) => state.value)
  // If the state value is not in the list of state values, it is not a valid US state
  return address.state && !stateValues.includes(address.state)
}

export const shouldAssumeCityAsLocationName = ({
  onsiteFormat,
  address,
}: {
  onsiteFormat: LocationMatcherDisplayFormats | undefined
  address: Address
}) => {
  return onsiteFormat === LocationMatcherDisplayFormats.DROPDOWN_MENU_TO_RADIO_BUTTONS && isOUSAddress(address)
}

/**
 * Generate unique list of dropdown locations.
 * Suppress state if country is OUS and customer is Google.
 */
export const getConstantOnsiteDropdownOptions = (
  addressOptions: Address[],
  onsiteFormat: LocationMatcherDisplayFormats | undefined,
) => {
  return uniqBy(
    addressOptions.sort((address1: Address, address2: Address) => {
      return address1.city >= address2.city ? 1 : -1
    }),
    'city',
  ).map((address: Address, idx: number) => {
    if (shouldAssumeCityAsLocationName({ onsiteFormat, address })) {
      return {
        label: address.city,
        value: idx + 1,
      }
    } else {
      return {
        label: `${address.city}, ${address.state}`,
        value: idx + 1,
      }
    }
  })
}

/**
 * LocationMatcherDisplayFormats.RADIO_BUTTONS_ONLY
 * Return list of all possible onsite providers
 */
export const getConstantOnsiteLocationRadioOptions = (addressOptions: Address[]) => {
  const orderedAddresses = orderBy(addressOptions, ['name'], 'asc')
  const onsiteAddresses = compact(
    orderedAddresses.map((address: Address, idx: number) => {
      return address.id
        ? {
            label: `${address.name}`,
            value: address.id,
            id: idx + 1,
          }
        : undefined
    }),
  )
  return onsiteAddresses
}

/**
 * LocationMatcherDisplayFormats.DROPDOWN_MENU_TO_RADIO_BUTTONS
 * Return list of filtered onsite providers from specific 'city,state'.
 * If the customer is Google and address is OUS, only filter by 'city'.
 */
export const generateOnsiteLocationRadioOptions = (
  addressOptions: Address[],
  location: string,
  onsiteFormat: LocationMatcherDisplayFormats | undefined,
) => {
  if (location) {
    const filteredAddresses = addressOptions.filter((address: Address) => {
      let loc
      if (shouldAssumeCityAsLocationName({ onsiteFormat, address })) {
        loc = address.city
      } else {
        loc = `${address.city}, ${address.state}`
      }
      return location === loc
    })

    const sortedAddresses = orderBy(filteredAddresses, ['name'], 'asc')

    return compact(
      sortedAddresses.map((address: Address) =>
        address.id
          ? {
              label: address.name,
              value: address.id,
            }
          : undefined,
      ),
    )
  } else {
    return []
  }
}

export const getGooglePlacesAutocompleteFromFreeText = (
  jwt: string,
  textStr: string,
  isUserInternational: boolean,
  userCountryIsoCode?: string,
  isGooglePlacesAPIEnabled?: boolean,
) => {
  const url = isGooglePlacesAPIEnabled
    ? `${GOOGLE_PLACES_UPDATED_API_URL}/v1/places:autocomplete`
    : `${GOOGLE_PLACES_VENDOR_API_URL}/place/autocomplete/json`

  const headersConfig = {
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  }
  const config = {
    ...headersConfig,
    params: {
      input: textStr,
      components: isUserInternational ? `country:${userCountryIsoCode}` : 'country:us|country:pr',
    },
  }
  const axiosInstance = axios.create()
  axiosRetry(axiosInstance, {
    retries: 3,
    retryCondition: (_isNetworkOrIdempotentRequestError) => true, // retry no matter what
  })
  if (isGooglePlacesAPIEnabled) {
    return axiosInstance.post(
      url,
      {
        input: textStr,
        includedRegionCodes: isUserInternational ? [userCountryIsoCode] : ['US', 'PR'],
      },
      headersConfig,
    )
  } else {
    return axiosInstance.get(url, config)
  }
}

export const getGooglePlaceDetailsWithPlaceId = (jwt: string, placeId: string, isGooglePlacesAPIEnabled?: boolean) => {
  const url = isGooglePlacesAPIEnabled
    ? `${GOOGLE_PLACES_UPDATED_API_URL}/v1/places/${placeId}`
    : `${GOOGLE_PLACES_VENDOR_API_URL}/place/details/json`

  const config = {
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
    params: isGooglePlacesAPIEnabled
      ? { fields: 'location,formattedAddress,addressComponents' }
      : {
          place_id: placeId,
        },
  }
  const axiosInstance = axios.create()
  axiosRetry(axiosInstance, {
    retries: 3,
    retryCondition: (_isNetworkOrIdempotentRequestError) => true, // retry no matter what
  })

  return axiosInstance.get(url, config)
}

export const checkValidGeoLocationAndGrabDetails = ({
  jwt,
  textStr,
  isUserInternational,
  userCountryIsoCode,
  logToSumo,
  isGooglePlacesAPIEnabled,
}: {
  jwt: string
  textStr: string
  isUserInternational: boolean
  userCountryIsoCode?: string
  logToSumo?: (message: string, extras?: Dict, error?: Error | undefined) => void
  isGooglePlacesAPIEnabled?: boolean
}): any => {
  return new Promise(function (resolve, reject) {
    getGooglePlacesAutocompleteFromFreeText(
      jwt,
      textStr,
      isUserInternational,
      userCountryIsoCode,
      isGooglePlacesAPIEnabled,
    )
      .then((response) => {
        const results = isGooglePlacesAPIEnabled ? response?.data?.suggestions : response?.data?.predictions
        if (results?.length >= 1) {
          const placeId = isGooglePlacesAPIEnabled
            ? response?.data?.suggestions[0]?.placePrediction?.placeId
            : response?.data?.predictions[0]?.place_id
          if (placeId) {
            getGooglePlaceDetailsWithPlaceId(jwt, placeId, isGooglePlacesAPIEnabled)
              .then((response) => {
                const details = isGooglePlacesAPIEnabled ? response.data : response.data.result
                return resolve(details)
              })
              .catch((error) => {
                logToSumo &&
                  logToSumo(`Location Error: Could not get a place details: ${error}`, {
                    textStr,
                    isUserInternational,
                    userCountryIsoCode,
                  })
                reject(error)
              })
          } else {
            logToSumo &&
              logToSumo('Location Error: Could not get a place id', {
                placeId,
                textStr,
                isUserInternational,
                userCountryIsoCode,
              })
            return resolve(null)
          }
        } else {
          logToSumo &&
            logToSumo(`Location Error: There were no predictions: ${response}`, {
              textStr,
              isUserInternational,
              userCountryIsoCode,
            })
          return resolve(null)
        }
      })
      .catch((error) => {
        logToSumo &&
          logToSumo(`Location Error: Could not get an autocomplete result: ${error}`, {
            textStr,
            isUserInternational,
            userCountryIsoCode,
          })
        reject(error)
      })
  })
}
